import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Col, Row, Card, Container } from "react-bootstrap"
import { useParams } from "react-router"
import SettingSections from "@/components/Settings/SettingSections"
import UserComponent from "@/components/Settings/UserComponent"
import RoleComponent from "@/components/Settings/RoleComponent"
import CategoryComponent from "@/components/Settings/CategoryComponent"
import StagesComponent from "@/components/Settings/StagesComponent"
import PlaceStatusComponent from "@/components/Settings/PlaceStatusComponent"
import PlaceVisitComponent from "@/components/Settings/PlaceVisitComponent"
import MapProviderSettings from "@/components/Settings/MapProviderSettings"
import IncidentNotificationsSettings from "@/components/Settings/IncidentNotificationsSettings"
import CompanyEmailLogSettings from "@/components/Settings/CompanyEmailLogSettings"

interface SettingsDTO {
  id: string
}

const iconMap: Record<string, { icon: string; color: string; desc: string }> = {
  users: {
    icon: "fas fa-users",
    color: "text-primary",
    desc: "Gestión de usuarios y accesos a la organización",
  },
  roles: {
    icon: "fas fa-user-shield",
    color: "text-info",
    desc: "Perfiles y roles de permisos",
  },
  stages: {
    icon: "fas fa-layer-group",
    color: "text-secondary",
    desc: "Etapas del pipeline y embudo de prospección",
  },
  categories: {
    icon: "fas fa-tags",
    color: "text-warning",
    desc: "Tipos y clasificaciones de comercios / lugares",
  },
  status: {
    icon: "fas fa-traffic-light",
    color: "text-danger",
    desc: "Estados de prospección, visita y venta",
  },
  placeVisit: {
    icon: "fas fa-map-pin",
    color: "text-success",
    desc: "Configuración de reglas para visitas de campo",
  },
  mapProvider: {
    icon: "fas fa-map-marked-alt",
    color: "text-primary",
    desc: "Seleccionar proveedor de mapas (Google, OSM, Mapbox)",
  },
  incidentNotifications: {
    icon: "fas fa-bell",
    color: "text-warning",
    desc: "Destinatarios y alertas de incidencias de campo",
  },
  emailLog: {
    icon: "fas fa-envelope-open-text",
    color: "text-success",
    desc: "Historial de correos enviados y pruebas de digests semanales",
  },
}

const SettingsPage = () => {
  const { id } = useParams<SettingsDTO>()
  const [section, setSection] = useState("")
  const loadedSettings = SettingSections
  const [t] = useTranslation("global")

  const toggle = (obj: string) => {
    setSection(obj)
  }

  return (
    <>
      {/* Modal Dialogs */}
      {section === "users" && <UserComponent show={true} toggle={toggle} />}
      {section === "roles" && <RoleComponent show={true} toggle={toggle} />}
      {section === "categories" && <CategoryComponent show={true} toggle={toggle} />}
      {section === "stages" && <StagesComponent show={true} toggle={toggle} />}
      {section === "status" && <PlaceStatusComponent show={true} toggle={toggle} />}
      {section === "placeVisit" && <PlaceVisitComponent show={true} toggle={toggle} />}
      {section === "mapProvider" && <MapProviderSettings show={true} toggle={toggle} />}
      {section === "incidentNotifications" && (
        <IncidentNotificationsSettings show={true} toggle={toggle} />
      )}
      {section === "emailLog" && <CompanyEmailLogSettings show={true} toggle={toggle} />}

      {/* Main Settings Grid */}
      <Container className="py-4">
        <div className="mb-4">
          <h4 className="fw-bold text-dark mb-1">
            <i className="fas fa-sliders-h text-primary me-2" />
            {t("Configuración de la Empresa")}
          </h4>
          <p className="text-muted small mb-0">
            {t("Administra los parámetros generales, usuarios, mapas y notificaciones de tu plataforma.")}
          </p>
        </div>

        {Array.isArray(loadedSettings) &&
          loadedSettings.map((mainSection, index) => {
            if (mainSection.requiredParent && mainSection.parentId !== id) return null
            return (
              <div key={index} className="mb-4">
                <h6 className="text-uppercase text-muted fw-bold mb-3 small tracking-wide">
                  {t(mainSection.name)}
                </h6>
                <Row className="g-3">
                  {mainSection.sections.map((subSection, subSectionIndex) => {
                    const meta = iconMap[subSection.reference] || {
                      icon: "fas fa-cog",
                      color: "text-primary",
                      desc: "Parámetros y opciones del sistema",
                    }
                    return (
                      <Col xs={12} sm={6} md={4} key={subSectionIndex}>
                        <Card
                          className="h-100 shadow-sm border rounded-3 cursor-pointer hover-shadow transition-all"
                          style={{ cursor: "pointer" }}
                          onClick={() => toggle(subSection.reference)}
                        >
                          <Card.Body className="p-3 d-flex flex-column justify-content-between">
                            <div>
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <div
                                  className="p-2 rounded-circle bg-light border d-flex align-items-center justify-content-center"
                                  style={{ width: "40px", height: "40px" }}
                                >
                                  <i className={`${meta.icon} ${meta.color} fa-lg`} />
                                </div>
                                <i className="fas fa-chevron-right text-muted small" />
                              </div>
                              <h6 className="fw-bold text-dark mb-1">{t(subSection.label)}</h6>
                              <p className="text-muted small mb-0" style={{ fontSize: "12px" }}>
                                {t(meta.desc)}
                              </p>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )
                  })}
                </Row>
              </div>
            )
          })}
      </Container>
    </>
  )
}

export default SettingsPage
